import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// ─── EXISTING UTILITIES ─────────────────────────────────────────────────────

export const fadeUp = (targets, options = {}) => {
  return gsap.fromTo(
    targets,
    { y: 50, opacity: 0 },
    {
      y: 0,
      opacity: 1,
      duration: 0.8,
      ease: 'power3.out',
      stagger: options.stagger !== undefined ? options.stagger : 0.12,
      scrollTrigger: {
        trigger: targets,
        start: 'top 95%',
        toggleActions: 'play none none reverse',
        fastScrollEnd: true,
      },
    }
  );
};

export const staggerCards = (targets, options = {}) => {
  return gsap.fromTo(
    targets,
    { y: 60, opacity: 0, rotation: options.rotation || 3 },
    {
      y: 0,
      opacity: 1,
      rotation: 0,
      stagger: options.stagger || 0.15,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: targets,
        start: 'top 92%',
        toggleActions: 'play none none reverse',
        fastScrollEnd: true,
      },
    }
  );
};

export const clipWipe = (target) => {
  return gsap.fromTo(
    target,
    { clipPath: 'inset(0 100% 0 0)' },
    {
      clipPath: 'inset(0 0% 0 0)',
      duration: 0.9,
      ease: 'power4.inOut',
      scrollTrigger: {
        trigger: target,
        start: 'top 95%',
        toggleActions: 'play none none reverse',
        fastScrollEnd: true,
      },
    }
  );
};

export const sectionBadge = (target) => {
  return gsap.fromTo(
    target,
    { scale: 0, opacity: 0 },
    {
      scale: 1,
      opacity: 1,
      ease: 'elastic.out(1, 0.5)',
      duration: 0.6,
      scrollTrigger: {
        trigger: target,
        start: 'top 95%',
        toggleActions: 'play none none reverse',
        fastScrollEnd: true,
      },
    }
  );
};

export const floatLoop = (target, yAmount, duration) => {
  return gsap.to(target, {
    y: yAmount,
    repeat: -1,
    yoyo: true,
    ease: 'sine.inOut',
    duration: duration,
  });
};

export const brutalistBtn = (selector) => {
  const elements = document.querySelectorAll(selector);
  elements.forEach((el) => {
    el.addEventListener('mouseenter', () => {
      gsap.to(el, { x: 4, y: 4, duration: 0.15 });
    });
    el.addEventListener('mouseleave', () => {
      gsap.to(el, { x: 0, y: 0, ease: 'elastic.out(1, 0.5)', duration: 0.3 });
    });
  });
};

export const cardHoverLift = (selector) => {
  const elements = document.querySelectorAll(selector);
  elements.forEach((el) => {
    el.addEventListener('mouseenter', () => {
      gsap.to(el, { x: -4, y: -4, duration: 0.2 });
    });
    el.addEventListener('mouseleave', () => {
      gsap.to(el, { x: 0, y: 0, ease: 'elastic.out(1, 0.6)', duration: 0.3 });
    });
  });
};

export const parallaxEl = (target, speed = 0.5) => {
  return gsap.to(target, {
    yPercent: -30 * speed,
    ease: 'none',
    scrollTrigger: {
      trigger: target,
      start: 'top bottom',
      end: 'bottom top',
      scrub: 1,
    },
  });
};

// ─── NEW ANIMATION UTILITIES ─────────────────────────────────────────────────

/**
 * magneticHover — Subtle magnetic pull towards cursor.
 * Use on: logo mark, hire-me button, hero CTAs.
 */
export const magneticHover = (selector) => {
  const elements = document.querySelectorAll(selector);

  elements.forEach((el) => {
    el.addEventListener('mousemove', (e) => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;

      gsap.to(el, {
        x: x * 0.35,
        y: y * 0.35,
        duration: 0.3,
        ease: 'power2.out',
      });
    });

    el.addEventListener('mouseleave', () => {
      gsap.to(el, {
        x: 0,
        y: 0,
        duration: 0.6,
        ease: 'elastic.out(1, 0.5)',
      });
    });
  });
};

/**
 * scrambleText — Randomised character scramble effect on a heading.
 * Call onEnter of a ScrollTrigger.
 * @param {Element|string} target - DOM element or CSS selector
 * @param {string} finalText - The resolved text to land on
 */
export const scrambleText = (target, finalText) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const el = typeof target === 'string'
    ? document.querySelector(target) : target;
  if (!el) return;

  let iteration = 0;
  const interval = setInterval(() => {
    el.textContent = finalText
      .split('')
      .map((char, i) => {
        if (i < iteration) return finalText[i];
        if (char === ' ') return ' ';
        return chars[Math.floor(Math.random() * chars.length)];
      })
      .join('');

    if (iteration >= finalText.length) {
      clearInterval(interval);
    }
    iteration += 0.5;
  }, 30);
};

/**
 * animateCounter — Number counter with expo.out easing.
 * Replaces old counterAnim; more dramatic snap-in feel.
 * @param {Element|string} target - DOM element or CSS selector
 * @param {number} end - Final number value
 * @param {string} [suffix=''] - Suffix appended (e.g. '+', '%')
 * @param {number} [duration=2] - Animation duration in seconds
 */
export const animateCounter = (target, end, suffix = '', duration = 2) => {
  const el = typeof target === 'string'
    ? document.querySelector(target) : target;
  if (!el) return;

  gsap.fromTo(
    { val: 0 },
    { val: 0 },
    {
      val: end,
      duration,
      ease: 'expo.out',
      onUpdate: function () {
        el.textContent = Math.round(this.targets()[0].val) + suffix;
      },
      scrollTrigger: {
        trigger: el,
        start: 'top 92%',
        once: true,
        fastScrollEnd: true,
      },
    }
  );
};

/**
 * maskReveal — Cinematic reveal by sliding content out of a hidden wrapper.
 * More premium than plain clipPath fades.
 * @param {Element|string} target - DOM element or CSS selector
 * @param {{ delay?: number, direction?: 'up'|'down' }} [options]
 */
export const maskReveal = (target, options = {}) => {
  const { delay = 0, direction = 'up' } = options;

  const el = typeof target === 'string'
    ? document.querySelector(target) : target;
  if (!el) return;

  // Wrap element in overflow-hidden mask container
  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'overflow:hidden; display:inline-block; width:100%';
  el.parentNode.insertBefore(wrapper, el);
  wrapper.appendChild(el);

  const fromY = direction === 'up' ? '100%'
    : direction === 'down' ? '-100%' : '0%';

  gsap.fromTo(
    el,
    { y: fromY, opacity: 0 },
    {
      y: '0%',
      opacity: 1,
      duration: 0.9,
      delay,
      ease: 'power4.out',
      scrollTrigger: {
        trigger: wrapper,
        start: 'top 92%',
        toggleActions: 'play none none reverse',
        fastScrollEnd: true,
      },
    }
  );
};
